import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    forwardRef,
    input,
    output,
    signal,
    viewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';

/**
 * Design-system checkbox (Figma node 1973:11584, component set 190:6169).
 * 24px hit area, 18px box with 4px radius; selected state fills with the brand
 * `--new-primary-color` (white-label — matches legacy checkbox behavior) and shows a
 * white check. Border is 1px, not the Figma-spec'd 2px — thinned per design feedback,
 * it read too heavy in dense grid rows. Implements ControlValueAccessor so it works
 * with `formControlName`, `[(ngModel)]`, and template/reactive forms.
 *
 * @example
 * <lib-anc-checkbox [label]="'Enable'" formControlName="batch_management"></lib-anc-checkbox>
 */
@Component({
    selector: 'lib-anc-checkbox',
    imports: [CommonModule, TranslateModule],
    templateUrl: './anc-checkbox.component.html',
    styleUrls: ['./anc-checkbox.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AncCheckboxComponent),
            multi: true
        }
    ]
})
export class AncCheckboxComponent implements ControlValueAccessor {
    /** Optional label text (run through the translate pipe). */
    label = input<string>('');

    /** Emits the new checked value when the user toggles the box. */
    readonly changed = output<boolean>();

    /** Explicit disabled override for standalone (non-reactive-form) usage, e.g. `[disabled]="expr"`. */
    disabledInput = input<boolean>(false, { alias: 'disabled' });

    /**
     * "Some but not all" visual state for select-all/bulk-selection headers (not a Figma
     * variant — extrapolated the same way most design systems layer it onto Checkbox).
     * Takes priority over `checked` visually; does not affect the underlying form value.
     */
    indeterminate = input<boolean>(false);

    private readonly inputRef =
        viewChild<ElementRef<HTMLInputElement>>('input');

    /** Internal checked state, driven by the form control or user clicks. */
    protected readonly checked = signal<boolean>(false);
    /** Internal disabled state, driven by the form control via `setDisabledState`. */
    protected readonly formDisabled = signal<boolean>(false);
    /** Effective disabled state — either the form control or the explicit input. */
    protected readonly disabled = computed(
        () => this.formDisabled() || this.disabledInput()
    );

    private onChange: (value: boolean) => void = () => {};
    private onTouched: () => void = () => {};

    constructor() {
        // `indeterminate` has no HTML attribute — it must be set as a DOM property.
        effect(() => {
            const input = this.inputRef()?.nativeElement;
            if (input) {
                input.indeterminate = this.indeterminate();
            }
        });
    }

    /** Toggle from the native input's change event. */
    protected onToggle(event: Event): void {
        const value = (event.target as HTMLInputElement).checked;
        this.checked.set(value);
        this.onChange(value);
        this.onTouched();
        this.changed.emit(value);
    }

    // --- ControlValueAccessor ---
    writeValue(value: boolean): void {
        this.checked.set(!!value);
    }

    registerOnChange(fn: (value: boolean) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        queueMicrotask(() => this.formDisabled.set(isDisabled));
    }
}
